import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import useFormHook from '@shared/hooks/useFormHook';
import LoadingButton from '@shared/components/LoadingButton';
import CustomDatePicker from '@shared/components/CustomDatePicker';

const schema = yup.object({
    effectiveDate: yup.mixed().required('Required'),
    untilDate: yup.mixed().required('Required'),
});

export default function InvestmentServicesForm({
    onClose,
    onConfirm,
    editData,
    personId,
    empId,
    contractId,
    typeActivity = 0,
}) {
    const { t } = useTranslation();
    const { modalRef } = useFormHook({}, onClose);

    const {
        register,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            effectiveDate: '',
            untilDate: '',
        },
    });

    useEffect(() => {
        if (editData) {
            reset({
                effectiveDate: editData.effectiveDate || '',
                untilDate: editData.untilDate || '',
            });
        } else {
            reset({
                effectiveDate: '',
                untilDate: '',
            });
        }
    }, [editData, reset]);

    const submit = async (formData) => {
        const toISODate = (value) => {
            if (!value) return null;
            const date = value instanceof Date ? value : new Date(value);
            if (Number.isNaN(date.getTime())) return null;
            return date.toISOString().split('T')[0];
        };

        const payload = {
            personId,
            empId,
            contractId,
            effectiveDate: toISODate(formData.effectiveDate),
            untilDate: toISODate(formData.untilDate),
            typeActivity,
        };

        await onConfirm(payload);
    };

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">
                            {editData ? t('common.edit') : t('common.add')}
                        </h4>
                        <form className="region-form" onSubmit={handleSubmit(submit)}>
                            <div className="flex-column">
                                <span className="span-bold">{t('common.fromDate')} <span className="required">*</span></span>
                                <CustomDatePicker
                                    name="effectiveDate"
                                    value={watch('effectiveDate')}
                                    onChange={(date) => setValue('effectiveDate', date, { shouldValidate: true })}
                                    errors={errors}
                                    widthClass="width-20"
                                    renderInPortal={true}
                                />
                                {errors.effectiveDate && <p className="text-danger">{errors.effectiveDate.message}</p>}

                                <span className="span-bold">{t('common.toDate')} <span className="required">*</span></span>
                                <CustomDatePicker
                                    name="untilDate"
                                    value={watch('untilDate')}
                                    onChange={(date) => setValue('untilDate', date, { shouldValidate: true })}
                                    errors={errors}
                                    widthClass="width-20"
                                    renderInPortal={true}
                                />
                                {errors.untilDate && <p className="text-danger">{errors.untilDate.message}</p>}
                            </div>

                            <div className="modal-footer">
                                <LoadingButton type="submit" className="form-btn btn-blue" loading={isSubmitting}>
                                    {t('common.save')}
                                </LoadingButton>
                                <button type="button" className="form-btn btn-red" onClick={onClose} disabled={isSubmitting}>
                                    {t('common.cancel')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

